import React, { useState, useMemo } from 'react'
import { ViewHeader, Card } from '@enkla-gui'
import faq from '@components/help/faq.json'

const CATEGORIES = [
  { key: 'integrationer', label: 'Integrationer', icon: 'fa-plug', color: 'blue' },
  { key: 'nyckeltal', label: 'Nyckeltal & Ekonomi', icon: 'fa-chart-line', color: 'teal' },
  { key: 'hyresavier', label: 'Hyresavier', icon: 'fa-file-invoice', color: 'amber' },
  { key: 'hyresgaster', label: 'Hyresgäster', icon: 'fa-users', color: 'purple' },
  { key: 'uthyrning', label: 'Uthyrning & Objekt', icon: 'fa-handshake', color: 'rose' },
  { key: 'konto', label: 'Konto & Allmänt', icon: 'fa-cog', color: 'slate' },
]

function FAQItem({ question, answer, open, onToggle }) {
  return (
    <div class={`faq-item ${open ? 'faq-item-open' : ''}`} onClick={onToggle}>
      <div class='faq-item-header'>
        <span class='faq-item-question'>{question}</span>
        <i class={`fas fa-chevron-${open ? 'up' : 'down'} faq-item-arrow`} />
      </div>
      {open && (
        <div class='faq-item-answer' onClick={e => e.stopPropagation()}>
          <div dangerouslySetInnerHTML={{ __html: answer }} />
        </div>
      )}
    </div>
  )
}

function FAQGroup({ category, items, openItems, onToggle, defaultOpen }) {
  const [expanded, setExpanded] = useState(defaultOpen || false)
  const cat = CATEGORIES.find(c => c.key === category)

  return (
    <div class='faq-group'>
      <div class='faq-group-header' onClick={() => setExpanded(!expanded)}>
        <div class='faq-group-left'>
          <div class={`faq-group-icon faq-color-${cat.color}`}>
            <i class={`fas ${cat.icon}`} />
          </div>
          <span class='faq-group-label'>{cat.label}</span>
          <span class='faq-group-count'>{items.length}</span>
        </div>
        <i class={`fas fa-chevron-${expanded ? 'up' : 'down'} faq-group-arrow`} />
      </div>
      {expanded && (
        <div class='faq-group-items'>
          {items.map((item, i) => (
            <FAQItem
              key={i}
              question={item.q}
              answer={item.a}
              open={openItems.has(item.q)}
              onToggle={() => onToggle(item.q)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function HelpFAQ() {
  const [search, setSearch] = useState('')
  const [openItems, setOpenItems] = useState(new Set())

  const groups = useMemo(() => {
    const grouped = {}
    for (const item of faq) {
      if (search && !item.q.toLowerCase().includes(search.toLowerCase())
        && !item.a.toLowerCase().includes(search.toLowerCase())) continue
      const cat = item.c || 'konto'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(item)
    }
    return CATEGORIES
      .filter(c => grouped[c.key]?.length)
      .map(c => ({ key: c.key, items: grouped[c.key] }))
  }, [search])

  const totalVisible = groups.reduce((sum, g) => sum + g.items.length, 0)

  const toggleItem = (q) => {
    setOpenItems(prev => {
      const next = new Set(prev)
      next.has(q) ? next.delete(q) : next.add(q)
      return next
    })
  }

  return (
    <>
      <ViewHeader icon='fa-info-circle' title='Hjälp' subtitle='FAQ' />

      <div class='faq-hero'>
        <Card>
          <div class='faq-hero-content'>
            <div class='faq-hero-icon'>
              <i class='fas fa-question-circle' />
            </div>
            <div class='faq-hero-text'>
              <h2>Vanliga frågor</h2>
              <p>
                Här hittar du svar på de vanligaste frågorna om Enkla Fastigheter.
                Skulle du inte hitta det du söker kan du alltid{' '}
                <a href='/#!/help/contact'>kontakta oss</a> så hjälper vi dig.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <div class='faq-search'>
          <i class='fas fa-search' />
          <input
            type='text'
            placeholder='Sök bland frågor...'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <span class='faq-search-count'>{totalVisible} träffar</span>
          )}
        </div>
        <div class='faq-groups'>
          {groups.map(g => (
            <FAQGroup
              key={g.key}
              category={g.key}
              items={g.items}
              openItems={openItems}
              onToggle={toggleItem}
              defaultOpen={!!search}
            />
          ))}
          {groups.length === 0 && (
            <p class='faq-empty'>Inga frågor matchar din sökning.</p>
          )}
        </div>
      </Card>
    </>
  )
}
